@if ({cbProject: cbProject$ | async}; as state) {
  @if (loading$ | async) {
    <!-- --------- LOADING --------- -->
    <div class="oph-loading-container">
      <oph-loading></oph-loading>
    </div>
    <!-- --------- PROJECT NOT FOUND --------- -->
  } @else if (projectNotFound()) {
    <div class="project-not-found-container">
      <div class="project-not-found-content">
        <h1 class="project-not-found-title">Project not found</h1>
        <p class="project-not-found-message">The project you're looking for doesn't exist or may have been removed.</p>
        <oph-button
          [style]="{borderRadius: '8px', height: '56px', width: '100%', maxWidth: '300px', fontSize: '18px'}"
          color="green"
          size="large"
          (buttonClick)="onBackToProjects()"
        >
          Back
        </oph-button>
      </div>
    </div>
    <!-- --------- OVERVIEW --------- -->
  } @else if (currentStep() === 'overview') {
    <cb-overview></cb-overview>
  } @else {
    <!-- --------- CHALLENGE BUILDER --------- -->
    <div class="challenge-builder-container">
      <!-- Left Container (Flexible) -->
      <div class="left-container" [@.disabled]="animationsDisabled()">
        <div class="ai-toggle-container">
          <span class="ai-toggle-label">AI</span>
          <oph-slide-toggle
            [checked]="state.cbProject?.challengeBuilder?.useAi"
            [loading]="loadingAiToggle()"
            (toggle)="onToggleAi()"
          ></oph-slide-toggle>
        </div>
        <!-- Stepper -->
        <div class="stepper-container" #stepperContainer [style.--animation-duration.ms]="animationDuration">
          @for (step of steps(); track step; let i = $index; let isFirst = $first; let isLast = $last) {
            <button
              class="step-wrapper"
              [class.first]="isFirst"
              [class.last]="isLast"
              [class.animating]="animatingStepIndex() === i"
              [class.reverse-animating]="reverseAnimatingStepIndex() === i"
              [class.active]="i <= stepIndex()"
              [class.disabled]="isStepDisabled(i) || loadingStepIndex() !== null"
              [disabled]="isStepDisabled(i) || loadingStepIndex() !== null"
              (click)="onStepClick(step)"
            >
              @if (!isFirst) {
                <svg
                  class="step-cap-left"
                  width="13.5"
                  height="35"
                  viewBox="0 0 13.5 35"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 0H10L0 17.5L10 35H0V0Z"
                    [attr.fill]="
                      i <= stepIndex() || reverseAnimatingStepIndex() === i
                        ? challengeBuilderData[step].color
                        : defaultStepColor
                    "
                  />
                </svg>
              }

              <span
                class="step-body"
                [style.--step-color]="challengeBuilderData[step].color"
                [style.--default-color]="defaultStepColor"
              >
                <span
                  class="step-fill"
                  [class.filled]="i < stepIndex() || (i === stepIndex() && animatingStepIndex() !== i)"
                  [class.animating]="animatingStepIndex() === i"
                  [class.reverse-animating]="reverseAnimatingStepIndex() === i"
                ></span>
                <span class="step-label">
                  @if (loadingStepIndex() === i) {
                    <oph-loading [diameter]="18" [strokeWidth]="2" color="white"></oph-loading>
                  } @else {
                    {{ (stepMap[step]?.label | titlecase) || 'Finish' }}
                  }
                </span>
              </span>

              @if (!isLast) {
                <svg
                  class="step-cap-right"
                  width="10"
                  height="35"
                  viewBox="0 0 10 35"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 0L10 17.5L0 35V0Z"
                    [attr.fill]="
                      i < stepIndex() || (i === stepIndex() && animatingStepIndex() !== i)
                        ? challengeBuilderData[step].color
                        : defaultStepColor
                    "
                  />
                </svg>
              }
            </button>
          }
        </div>

        <div class="left-container__header" [@sectionSlide]="stepIndex()">
          <!-- Mobile Drawer Toggle Button -->
          <button class="mobile-drawer-toggle" (click)="openDrawer()">
            <oph-icon name="list-orange" [size]="24"></oph-icon>
          </button>

          <div class="header-icon-container">
            <img
              class="header-icon"
              [src]="'assets/img/icons/challenge-builder/' + currentStepData().icon"
              [alt]="currentStepData().label + ' icon'"
            />
            <img class="header-icon-background" src="assets/img/icons/challenge-builder/icon-background.png" alt="" />
          </div>
          <div class="header-content">
            <h1 class="header-title">{{ currentStepData().titleText }}</h1>
            <p class="header-subtitle">{{ currentStepData().subtitleText }}</p>
          </div>
        </div>

        <div class="content-container" [@contentSlide]="stepIndex()">
          @switch (currentStep()) {
            @case ('goal') {
              <cb-goal
                [nextClick$]="nextClick$"
                [backClick$]="backClick$"
                [cbProject]="state.cbProject"
                (onNext)="onNext()"
                (onBack)="onBack()"
              ></cb-goal>
            }
            @case ('objectives') {
              <cb-objectives
                [nextClick$]="nextClick$"
                [backClick$]="backClick$"
                [cbProject]="state.cbProject"
                (onNext)="onNext()"
                (onBack)="onBack()"
              ></cb-objectives>
            }
            @case ('tasks') {
              <cb-tasks
                [nextClick$]="nextClick$"
                [backClick$]="backClick$"
                [cbProject]="state.cbProject"
                (onNext)="onNext()"
                (onBack)="onBack()"
              ></cb-tasks>
            }
            @case ('metrics') {
              <cb-metrics
                [nextClick$]="nextClick$"
                [backClick$]="backClick$"
                [cbProject]="state.cbProject"
                [step]="currentStep()"
                (onNext)="onNext()"
                (onBack)="onBack()"
              ></cb-metrics>
            }
            @case ('targets') {
              <cb-targets
                [nextClick$]="nextClick$"
                [backClick$]="backClick$"
                [cbProject]="state.cbProject"
                (onNext)="onNext()"
                (onBack)="onBack()"
              ></cb-targets>
            }
            @case ('schedule') {
              <cb-schedule
                [nextClick$]="nextClick$"
                [backClick$]="backClick$"
                [cbProject]="state.cbProject"
                [step]="currentStep()"
                (onNext)="onNext()"
                (onBack)="onBack()"
              ></cb-schedule>
            }
          }
        </div>

        @if (state.cbProject?.challengeBuilder?.goals?.length || !state.cbProject?.challengeBuilder?.useAi) {
          <div class="left-container__footer" [@sectionSlide]="stepIndex()">
            <oph-button
              class="back-button"
              [style]="{borderRadius: '8px', height: '46px', width: '100%'}"
              colorHex="#c7c2b6"
              colorHexText="#ffffff"
              size="large"
              [loading]="backLoading$ | async"
              [disabled]="backLoading$ | async"
              (buttonClick)="onBackClick()"
            >
              {{ currentStep() === 'goal' ? 'CANCEL' : 'BACK' }}
            </oph-button>

            <oph-button
              class="next-button"
              [style]="{borderRadius: '8px', height: '46px', width: '100%'}"
              color="green"
              size="large"
              [loading]="nextLoading$ | async"
              [disabled]="(challengeNextValid$ | async) === false || (nextLoading$ | async)"
              (buttonClick)="onNextClick()"
            >
              <div class="next-button-content">
                <span class="next-button-label">NEXT</span>
                <span class="next-button-subtext">{{ stepMap[currentStep()].nextButtonText }}</span>
              </div>
            </oph-button>
          </div>
        }
      </div>

      <!-- Right Sidebar (Fixed Width) -->
      <div class="right-sidebar">
        <cb-sidebar [currentStep]="$any(currentStep())" [cbProject]="state.cbProject"></cb-sidebar>
      </div>

      <!-- Mobile Drawer Overlay -->
      @if (drawerOpen()) {
        <div class="drawer-overlay" (click)="closeDrawer()"></div>
      }

      <!-- Mobile Drawer -->
      <div class="mobile-drawer" [class.open]="drawerOpen()">
        <div class="drawer-header">
          <oph-x-button (buttonClick)="closeDrawer()"></oph-x-button>
        </div>
        <cb-sidebar [currentStep]="$any(currentStep())" [cbProject]="state.cbProject"></cb-sidebar>
      </div>
    </div>
  }
}
